import { isNotSampled, parseBaggage } from './baggage'
import { instrumentFetch, type InstrumentFetchOpts } from './fetch'
import { nullTel } from './tel'
import type {
  BoundaryTelemetry,
  ContextPropagation,
  SpanTarget,
  Tel,
} from './tel-types'
import type { Attrs } from './attrs'

export const BOUNDARY_TELEMETRY_HEADER = 'x-boundary-telemetry'

const DEFAULT_MAX_PAYLOAD_BYTES = 131_072
const relayFetchTel = new WeakMap<Response, Tel>()

interface BoundaryRequestTel {
  tel: Tel
  end(result: {
    status: number
    error?: unknown
    attributes?: Attrs
    /** Span error severity (spans.md): a refusal the callee decided is a warning, a failure an error. */
    errorLevel?: 'warning' | 'error'
  }): BoundaryTelemetry | undefined
}

/**
 * Platform implementation detail shared by controlled upstream protocols.
 * Keeps mode validation, request ownership, HTTP attributes, and boundary
 * export lifecycle identical across RPC, DB sync, and bucket handlers.
 */
export function _createBoundaryRequestTel(
  rootTel: Tel,
  request: Request,
  opts: {
    mode: ContextPropagation
    name: string
    target: SpanTarget
    input?: unknown
    attributes?: Attrs
  },
): BoundaryRequestTel {
  // :uc[null-tel-boundary-helpers-build-nothing] nullTel reads no headers.
  if (rootTel === nullTel) {
    return { tel: nullTel, end: () => {} }
  }
  const inbound = parseBaggage(request.headers, {
    mode: opts.mode,
  })
  // :uc[not-sampled-flags-disable-callee-telemetry] OTel ParentBased: an unsampled parent disables this request.
  if (isNotSampled(inbound)) {
    return { tel: nullTel, end: () => {} }
  }

  const url = new URL(request.url)
  const tel = rootTel._createTel({
    ...inbound,
    trace: !inbound?.traceId,
    request: true,
    name: opts.name,
    target: opts.target,
    input: opts.input ?? { method: request.method, path: url.pathname },
    attributes: {
      'http.request.method': request.method,
      'url.path': url.pathname,
      ...opts.attributes,
    },
  })
  let ended = false

  return {
    tel,
    end(result) {
      if (ended) return void 0
      ended = true
      tel.currentSpan.end({
        output: { status: result.status },
        error: result.error,
        attributes: {
          'http.response.status_code': result.status,
          ...result.attributes,
        },
      }, result.errorLevel ? { errorLevel: result.errorLevel } : undefined)
      return tel._exportBoundary()
    },
  }
}

/** Attaches a serialized request unit to an outbound response. */
export async function attachBoundaryTelemetry(
  response: Response,
  telemetry: BoundaryTelemetry | undefined,
  opts: { maxPayloadBytes?: number; tel?: Tel } = {},
): Promise<Response> {
  if (!telemetry) return response

  const contentType = response.headers.get('content-type')?.toLowerCase()
  if (contentType?.includes('application/json')) {
    const body = (await response.json()) as unknown
    if (_isPlainObject(body)) {
      if (
        _byteLength(JSON.stringify(telemetry)) >
        (opts.maxPayloadBytes ?? DEFAULT_MAX_PAYLOAD_BYTES)
      ) {
        opts.tel?.currentSpan.update({
          attributes: { 'tel.relay.dropped': 'payload-size' },
        })
        return _copyResponse(response, JSON.stringify(body))
      }
      const serialized = JSON.stringify(
        attachBoundaryTelemetryToBody(body, telemetry),
      )
      return _copyResponse(response, serialized, undefined, {}, true)
    }
    return _copyResponse(response, JSON.stringify(body), telemetry, opts)
  }

  return _copyResponse(response, response.body, telemetry, opts)
}

/** Attaches telemetry to a caller-owned plain JSON object. */
export function attachBoundaryTelemetryToBody<
  T extends Record<string, unknown>,
>(
  body: T,
  telemetry?: BoundaryTelemetry,
): T & { telemetry?: BoundaryTelemetry } {
  return telemetry ? { ...body, telemetry } : body
}

/** Creates an instrumented fetch and remembers its span per response. */
export function createRelayFetch(opts: InstrumentFetchOpts): NonNullable<InstrumentFetchOpts['fetch']> {
  // nullTel: no span, no response registration; instrumentFetch only stamps flags 00.
  if (opts.tel === nullTel) return instrumentFetch(opts)
  return instrumentFetch({
    ...opts,
    onResponse(response, tel) {
      relayFetchTel.set(response, tel)
      opts.onResponse?.(response, tel)
    },
  })
}

export function getRelayFetchTel(response: Response): Tel | undefined {
  return relayFetchTel.get(response)
}

/** Inserts response telemetry below its registered fetch span. */
export function insertRelayedTelemetry<T>(response: Response, body?: T): T {
  const objectBody = _isPlainObject(body) ? body : undefined
  const bodyTelemetry = objectBody?.telemetry as BoundaryTelemetry | undefined
  const headerTelemetry = response.headers.get(BOUNDARY_TELEMETRY_HEADER)
  const telemetry = bodyTelemetry ?? _decodeTelemetry(headerTelemetry)
  if (telemetry) {
    getRelayFetchTel(response)?._insertBoundaryTrace(
      telemetry.trace,
      telemetry.metrics,
      telemetry.resource,
    )
  }

  if (!objectBody || !('telemetry' in objectBody)) return body as T
  const { telemetry: _telemetry, ...rest } = objectBody
  return rest as T
}

function _copyResponse(
  response: Response,
  body: BodyInit | null,
  telemetry?: BoundaryTelemetry,
  opts: { maxPayloadBytes?: number; tel?: Tel } = {},
  carriesTelemetry = false,
): Response {
  const headers = new Headers(response.headers)
  if (telemetry || carriesTelemetry) {
    // Add `no-store`, don't clobber whatever cache-control the response
    // owner already set (e.g. `private, no-store`): a telemetry-bearing
    // response must never be cached, but the route's own directives are
    // its own business, not the relay's.
    const existing = headers.get('Cache-Control')
    if (!existing?.includes('no-store')) {
      headers.set('Cache-Control', existing ? `${existing}, no-store` : 'no-store')
    }
  }
  if (telemetry) {
    const serialized = JSON.stringify(telemetry)
    const encoded = _encodeTelemetry(serialized)
    if (
      _byteLength(serialized) <=
      (opts.maxPayloadBytes ?? DEFAULT_MAX_PAYLOAD_BYTES)
    ) {
      headers.set(BOUNDARY_TELEMETRY_HEADER, encoded)
    } else {
      opts.tel?.currentSpan.update({
        attributes: { 'tel.relay.dropped': 'payload-size' },
      })
    }
  }
  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  })
}

function _isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const prototype: unknown = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

type Base64Bytes = Uint8Array & { toBase64?: () => string }
type Base64Decoder = typeof Uint8Array & { fromBase64?: (value: string) => Uint8Array }

/** Native base64 (TC39 Uint8Array base64: Bun, Chrome 140+, Safari 18.2+, Firefox 133+) where present: the
 * per-byte string build cost ~44 us for a 4.8 KB header; older browsers keep it. */
function _encodeTelemetry(serialized: string): string {
  const bytes: Base64Bytes = new TextEncoder().encode(serialized)
  if (bytes.toBase64) return bytes.toBase64()
  let binary = ''
  for (const byte of bytes) binary += String.fromCodePoint(byte)
  return btoa(binary)
}

function _byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

function _decodeTelemetry(value: string | null): BoundaryTelemetry | undefined {
  if (!value) return undefined
  try {
    const decoder: Base64Decoder = Uint8Array
    const bytes = decoder.fromBase64
      ? decoder.fromBase64(value)
      : Uint8Array.from(atob(value), (char) => char.codePointAt(0) ?? 0)
    return JSON.parse(new TextDecoder().decode(bytes)) as BoundaryTelemetry
  } catch {
    return undefined
  }
}
